import {
    calculateTotalPrice,
    isResourcePriceByTimeSteps,
} from "@@/shared/acquire/resource-price/resource-price-by-time-step";
import { convertTimeByGranularity } from "@@/shared/date-time/time-granularity";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import { zeroPrice } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { repeat } from "@@/utilities/repeat";
import { isResourcePriceByTime } from "../resource-price/resource-price-by-time";
import { isResourcePriceFixed } from "../resource-price/resource-price-fixed";
import type { BookableOption } from "./bookable-option";
import { hasResourcePrice } from "./bookable-option";
import type { BookableOptionValue } from "./bookable-option-value";

const bookableOptionPriceCalculator = (params: {
    readonly bookedMinutes: DurationInMinutes;
    readonly quantity: number;
    readonly option: BookableOptionValue | BookableOption;
}) => {
    const { option, quantity, bookedMinutes } = params;
    if (!hasResourcePrice<typeof option>(option)) return zeroPrice;
    if (quantity === 0) return zeroPrice;

    if (isResourcePriceFixed(option.resourcePrice)) {
        return sumPrices(repeat(option.resourcePrice.price, quantity));
    }
    if (isResourcePriceByTime(option.resourcePrice)) {
        const stepInMinutes = convertTimeByGranularity(
            option.resourcePrice.step,
            option.resourcePrice.granularity,
            "MINUTE",
        );
        const timeSteps = Math.ceil(bookedMinutes / stepInMinutes);
        const totalPrice = sumPrices(
            repeat(option.resourcePrice.price, timeSteps),
        );

        return sumPrices(repeat(totalPrice, quantity));
    }
    return zeroPrice;
};

const bookableOptionPricePerCalculator = (params: {
    readonly bookedMinutes: DurationInMinutes;

    readonly option: BookableOptionValue | BookableOption;

    readonly isSpanningDST: boolean;
}) => {
    const { option, bookedMinutes, isSpanningDST } = params;
    if (!hasResourcePrice<typeof option>(option)) return zeroPrice;

    if (isResourcePriceFixed(option.resourcePrice)) {
        return option.resourcePrice.price;
    }
    if (isResourcePriceByTime(option.resourcePrice)) {
        const stepInMinutes = convertTimeByGranularity(
            option.resourcePrice.step,
            option.resourcePrice.granularity,
            "MINUTE",
        );

        if (
            isSpanningDST &&
            (option.resourcePrice.granularity === "DAY" ||
                option.resourcePrice.granularity === "NIGHT")
        ) {
            const timeSteps = Math.ceil((bookedMinutes - 60) / stepInMinutes);
            const totalPrice = sumPrices(
                repeat(option.resourcePrice.price, timeSteps),
            );

            return totalPrice;
        }

        const timeSteps = Math.ceil(bookedMinutes / stepInMinutes);
        const totalPrice = sumPrices(
            repeat(option.resourcePrice.price, timeSteps),
        );

        return totalPrice;
    }

    if (isResourcePriceByTimeSteps(option.resourcePrice)) {
        const total = calculateTotalPrice(option.resourcePrice, bookedMinutes);

        return total;
    }

    return zeroPrice;
};

export { bookableOptionPriceCalculator, bookableOptionPricePerCalculator };
